import React, { useState, useEffect } from 'react';
import { Modal, Button, Input } from '@/components/ui';
import type { MaintenanceIssue, IssueStatus, IssueSeverity, IssuePriority } from '../types/maintenance-issues.types';
import { useIssues } from '../hooks/useIssues';
import { toast } from 'sonner';

interface IssueFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  item?: MaintenanceIssue | null;
  companyId: string;
}

const severityOptions: { value: IssueSeverity; label: string }[] = [
  { value: 'low', label: 'Thấp' },
  { value: 'medium', label: 'Trung bình' },
  { value: 'high', label: 'Cao' },
  { value: 'critical', label: 'Khẩn cấp' },
];

const priorityOptions: { value: IssuePriority; label: string }[] = [
  { value: 'low', label: 'Thấp' },
  { value: 'medium', label: 'Trung bình' },
  { value: 'high', label: 'Cao' },
  { value: 'critical', label: 'Khẩn cấp' },
];

export function IssueFormModal({ isOpen, onClose, item, companyId }: IssueFormModalProps) {
  const isEdit = !!item;
  const { createIssue, isCreating } = useIssues(companyId);

  const [formData, setFormData] = useState<Partial<MaintenanceIssue>>({
    issue_code: '',
    title: '',
    description: '',
    issue_type: 'plant_health',
    severity: 'medium',
    priority: 'medium',
    status_code: 'open',
    project_id: '',
    zone_id: '',
    reported_at: '',
  });

  useEffect(() => {
    if (isOpen) {
      if (item) {
        setFormData(item);
      } else {
        setFormData({
          issue_code: `ISS-${Date.now().toString().slice(-6).toUpperCase()}`,
          title: '',
          description: '',
          issue_type: 'plant_health',
          severity: 'medium',
          priority: 'medium',
          status_code: 'open',
          project_id: '',
          zone_id: '',
          reported_at: new Date().toISOString(),
          company_id: companyId,
        });
      }
    }
  }, [isOpen, item, companyId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (!isEdit) {
        await createIssue({ ...formData, company_id: companyId });
      }
      onClose();
      toast.success(isEdit ? 'Cập nhật sự cố thành công' : 'Báo cáo sự cố mới thành công');
    } catch (error: any) {
      toast.error('Lỗi: ' + error.message);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Cập nhật thông tin sự cố' : 'Báo cáo sự cố hiện trường mới'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-6 py-2">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Mã sự cố (Tự động)"
            required
            value={formData.issue_code || ''}
            onChange={(e) => setFormData({ ...formData, issue_code: e.target.value })}
          />
          <Input
            label="ID Dự án *"
            required
            value={formData.project_id || ''}
            onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
          />
        </div>

        <Input
          label="Tiêu đề sự cố *"
          required
          value={formData.title || ''}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          placeholder="VD: Cây chết hàng loạt tại Zone A"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Mức độ nghiêm trọng *</label>
            <select
              required
              className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-2.5 transition-all"
              value={formData.severity || ''}
              onChange={(e) => setFormData({ ...formData, severity: e.target.value as IssueSeverity })}
            >
              {severityOptions.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Độ ưu tiên xử lý *</label>
            <select
              required
              className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-2.5 transition-all"
              value={formData.priority || ''}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value as IssuePriority })}
            >
              {priorityOptions.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Loại sự cố"
            required
            value={formData.issue_type || ''}
            onChange={(e) => setFormData({ ...formData, issue_type: e.target.value })}
            placeholder="VD: plant_health, irrigation, security..."
          />
          <Input
            label="ID Khu vực (Zone)"
            value={formData.zone_id || ''}
            onChange={(e) => setFormData({ ...formData, zone_id: e.target.value })}
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">Mô tả chi tiết sự cố</label>
          <textarea
            className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-3 min-h-[100px]"
            placeholder="Mô tả bối cảnh, số lượng thiệt hại hoặc các dấu hiệu quan sát được..."
            value={formData.description || ''}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-slate-50">
          <Button variant="outline" type="button" onClick={onClose} disabled={isCreating}>Hủy</Button>
          <Button variant="primary" type="submit" isLoading={isCreating}>
            {isEdit ? 'Lưu thay đổi' : 'Gửi báo cáo'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
